// encoderConfig — pure H.264 encode-parameter selection for the timelapse
// mp4 export (growth-timelapse-share-2026-07-02.md §5.2 「エンコード:
// WebCodecs VideoEncoder (H.264) … X は mp4 (H.264) のみ受理」, T2 slice).
//
// Everything here is data → data (no WebCodecs calls) so the vitest node
// env can pin the exact codec strings / bitrates we ship. The thin adapter
// (`mp4Sink.ts`) walks the candidate list through
// `VideoEncoder.isConfigSupported` and configures the first hit.
//
// Profile policy (X 互換): Main first (better compression at the same
// bitrate, universally decodable including X's transcoder), then
// Constrained Baseline / Baseline as fallbacks for encoders that only
// expose the baseline profile. High profile is deliberately not requested
// — baseline/main is the compatibility floor the T2 spec calls for.
//
// Codec string form: `avc1.PPCCLL` — PP = profile_idc (4D main / 42
// baseline), CC = constraint flags (40 = constraint_set1 → Constrained
// Baseline), LL = level_idc in hex, picked from the H.264 level table for
// the actual output dimensions + fps (a hardcoded level would either
// reject big custom canvases or overclaim on tiny ones).

/** H.264 level table rows we care about (≤ 5.2 covers any output we emit). */
interface AvcLevel {
  /** level_idc as 2-digit lowercase hex (codec string tail). */
  readonly hex: string;
  /** Max macroblocks per frame. */
  readonly maxMbPerFrame: number;
  /** Max macroblocks per second. */
  readonly maxMbPerSec: number;
}

/** Levels 3.0 → 5.2 (ITU-T H.264 Table A-1, frame-size / throughput columns). */
const AVC_LEVELS: readonly AvcLevel[] = [
  { hex: '1e', maxMbPerFrame: 1_620, maxMbPerSec: 40_500 }, // 3.0
  { hex: '1f', maxMbPerFrame: 3_600, maxMbPerSec: 108_000 }, // 3.1 (720p30)
  { hex: '20', maxMbPerFrame: 5_120, maxMbPerSec: 216_000 }, // 3.2
  { hex: '28', maxMbPerFrame: 8_192, maxMbPerSec: 245_760 }, // 4.0 (1080p30)
  { hex: '2a', maxMbPerFrame: 8_704, maxMbPerSec: 522_240 }, // 4.2
  { hex: '32', maxMbPerFrame: 22_080, maxMbPerSec: 589_824 }, // 5.0
  { hex: '33', maxMbPerFrame: 36_864, maxMbPerSec: 983_040 }, // 5.1
  { hex: '34', maxMbPerFrame: 36_864, maxMbPerSec: 2_073_600 }, // 5.2
];

/** Bitrate policy: bits-per-pixel heuristic, clamped to a sane X-upload band. */
export const BITS_PER_PIXEL = 0.12;
export const MIN_BITRATE_BPS = 1_000_000;
export const MAX_BITRATE_BPS = 8_000_000;

/** Keyframe cadence — one IDR every ~2 s keeps seek/scrub snappy at small cost. */
export const KEYFRAME_INTERVAL_SECONDS = 2;

export interface EncodeDimensions {
  readonly width: number;
  readonly height: number;
  readonly fps: number;
}

const assertDims = ({ width, height, fps }: EncodeDimensions): void => {
  if (
    !Number.isInteger(width) ||
    !Number.isInteger(height) ||
    width < 2 ||
    height < 2 ||
    width % 2 !== 0 ||
    height % 2 !== 0 ||
    !Number.isFinite(fps) ||
    fps <= 0
  ) {
    throw new TypeError(`encoderConfig: invalid dims ${width}x${height}@${fps}`);
  }
};

/**
 * Smallest H.264 level that fits `width × height @ fps`, as the 2-digit
 * hex tail of the codec string. Falls back to the largest table row for
 * absurd inputs (the encoder's own `isConfigSupported` is the final gate).
 */
export const avcLevelHex = (dims: EncodeDimensions): string => {
  assertDims(dims);
  const mbPerFrame = Math.ceil(dims.width / 16) * Math.ceil(dims.height / 16);
  const mbPerSec = mbPerFrame * dims.fps;
  for (const level of AVC_LEVELS) {
    if (mbPerFrame <= level.maxMbPerFrame && mbPerSec <= level.maxMbPerSec) return level.hex;
  }
  return AVC_LEVELS[AVC_LEVELS.length - 1]?.hex ?? '34';
};

/** Target bitrate (bps) for the given output dimensions. */
export const selectBitrate = (dims: EncodeDimensions): number => {
  assertDims(dims);
  const raw = Math.round(dims.width * dims.height * dims.fps * BITS_PER_PIXEL);
  return Math.min(MAX_BITRATE_BPS, Math.max(MIN_BITRATE_BPS, raw));
};

/**
 * Ordered `VideoEncoderConfig` candidates (best first). All share the same
 * dimensions / bitrate / `avc` bitstream format (mp4-muxer consumes
 * length-prefixed `avc`, NOT Annex B) and differ only in profile.
 */
export const buildEncoderConfigCandidates = (dims: EncodeDimensions): VideoEncoderConfig[] => {
  assertDims(dims);
  const level = avcLevelHex(dims);
  const bitrate = selectBitrate(dims);
  // Main → Constrained Baseline → Baseline (see module header).
  const codecs = [`avc1.4d00${level}`, `avc1.4240${level}`, `avc1.4200${level}`];
  return codecs.map((codec) => ({
    codec,
    width: dims.width,
    height: dims.height,
    bitrate,
    framerate: dims.fps,
    // mp4 boxes carry avcC + length-prefixed NALs; Annex B would corrupt the mux.
    avc: { format: 'avc' as const },
    // Offline export — favor quality over latency.
    latencyMode: 'quality' as const,
  }));
};

/** Frames between keyframes ({@link KEYFRAME_INTERVAL_SECONDS} worth). */
export const keyFrameIntervalFrames = (fps: number): number => {
  if (!Number.isFinite(fps) || fps <= 0) {
    throw new TypeError(`keyFrameIntervalFrames: invalid fps ${fps}`);
  }
  return Math.max(1, Math.round(fps * KEYFRAME_INTERVAL_SECONDS));
};

/** Keyframe policy: frame 0 and every interval thereafter. */
export const shouldKeyFrame = (frameIndex: number, fps: number): boolean =>
  frameIndex % keyFrameIntervalFrames(fps) === 0;
